export type Language = "en" | "bg" | "ru";

// Sorting translations - will be merged into properties section

export const languages: { code: Language; name: string; flag: string }[] = [
  { code: "en", name: "English", flag: "\uD83C\uDDEC\uD83C\uDDE7" },
  { code: "bg", name: "\u0411\u044A\u043B\u0433\u0430\u0440\u0441\u043A\u0438", flag: "\uD83C\uDDE7\uD83C\uDDEC" },
  { code: "ru", name: "\u0420\u0443\u0441\u0441\u043A\u0438\u0439", flag: "\uD83C\uDDF7\uD83C\uDDFA" },
];

const en = {
  nav: {
    home: "Home",
    deals: "Search",
    analytics: "Analytics",
    howItWorks: "How It Works",
    signIn: "Sign In",
    getStarted: "Get Started",
    dashboard: "Dashboard",
    myFilters: "My Filters",
    myAlerts: "My Alerts",
    signOut: "Sign Out",
    favorites: "Favorites",
  },
  hero: {
    title: "Buying property? We're on your side.",
    subtitle:
      "The only service in Bulgaria that combines AI valuation with 97% accuracy and professional negotiators to save you thousands of euros and stress.",
    ctaPrimary: "Find & Evaluate Property",
    ctaSecondary: "View Services",
    stats: {
      properties: "Properties Analyzed",
      avgSavings: "Avg. saved per deal thanks to our algorithm",
      activeAlerts: "Active Alerts",
      citiesCovered: "Cities Covered",
    },
    banners: {
      banner1Title: "Afraid of overpaying for an apartment in Bulgaria?",
      banner1Description: "We'll calculate for free how much a square meter actually costs in your chosen area or city",
      banner1Bonus: "You'll also get a 20% discount on any of our services",
      banner1Cta: "Calculate",
      banner2Title: "Investing in Bulgarian real estate?",
      banner2Description:
        "Get exclusive analytics for free: a heat map of price per m² and real annual price growth (ROI) by districts of Sofia or the coast",
      banner2Bonus: "You'll also get a 20% discount on any of our services",
      banner2Cta: "Get Analytics",
    },
  },
  marketProblem: {
    title: "The market works against the buyer. We change the rules.",
    description:
      "Traditional brokers protect the seller or their commission. Dealigator works only for you — we reduce the price and guarantee the purity of the deal.",
    standard: {
      title: "Standard",
      seller: "Seller",
      sellerBroker: "Seller's Broker",
      wantHighPrice: "Both want a higher price",
      vs: "VS",
      lonelyBuyer: "Lonely Buyer",
    },
    dealigator: {
      title: "With Dealigator",
      buyer: "Buyer",
      aiLawyerNegotiator: "AI + Lawyer + Negotiator",
      fullProtection: "Full protection of your interests",
      vs: "VS",
      seller: "Seller",
    },
  },
  secretWeapon: {
    title: "We know the real price before negotiations.",
    description: "Our algorithm analyzes 50+ factors and evaluates the property with 97% accuracy.",
    benefit:
      'How does this help you? You go to the viewing with an "ace up your sleeve" — you know exactly how much the property is worth and have arguments to reduce the price.',
  },
  services: {
    title: "Full protection from search to key",
    aiAnalysis: {
      title: "AI Valuation & Analysis",
      description: "We find undervalued properties and show their real value, hidden risks, and rental potential.",
    },
    negotiation: {
      title: "Negotiations & Deal",
      description:
        "Our expert negotiates on your behalf. Goal: reduce the price by 10-15%. If we don't reduce it — the service is free.",
    },
    legal: {
      title: "Legal Security",
      description:
        "We check the property for encumbrances, prepare contracts, and protect your money in an escrow account.",
    },
  },
  battle: {
    title: "Why choose Dealigator?",
    broker: "Regular Broker",
    dealigator: "Dealigator",
    interest: { label: "Whose interest?", broker: "Seller's / Own", ai: "Only Yours (Buyer's)" },
    priceEval: { label: "Price evaluation", broker: '"By eye" / What seller says', ai: "AI Algorithm (97% accuracy)" },
    goalNeg: { label: "Negotiation goal", broker: "Quick deal", ai: "Maximum discount" },
    legalCheck: { label: "Legal check", broker: "Superficial", ai: "Deep (Encumbrances, history)" },
    pricing: { label: "Price", broker: "3-6% commission", ai: "2.5% or fixed fee (depends on the service)" },
  },
  featuredDeals: {
    badge: "New",
    title: "Latest Listings",
    subtitle: "The newest apartments on the market — be the first to see them.",
    viewAll: "View All",
    belowMarket: "below market",
    beds: "beds",
    baths: "baths",
    viewDeal: "View Deal",
  },
  analyticsPreview: {
    badge: "Smart Insights",
    title: "Your Personal Analytics Platform",
    subtitle:
      "Deep market insights, price predictions, and trend analysis to help you make informed investment decisions.",
    features: {
      priceAnalytics: {
        title: "Price Analytics",
        description: "Historical trends and price forecasting",
      },
      heatMaps: {
        title: "Heat Maps",
        description: "Visualize hot zones and emerging areas",
      },
      roiCalculator: {
        title: "ROI Calculator",
        description: "Estimate your investment returns",
      },
      dealAlerts: {
        title: "Deal Alerts",
        description: "Instant notifications for matching deals",
      },
    },
    exploreButton: "Explore Analytics",
    marketOverview: "Sofia Market Overview",
    pricePerSqm: "Price/sqm",
    listings: "Listings",
    avgDays: "Avg. Days",
    newDealFound: "New Deal Found!",
    matchesFilters: "Matches your filters",
  },
  howItWorks: {
    badge: "Simple Process",
    title: "How Dealigator Works",
    subtitle: "Three simple steps to finding your perfect property deal.",
    steps: {
      collect: {
        step: "01",
        title: "We Collect Data",
        description: "Our system aggregates listings from 20+ sources across Bulgaria, updating every hour.",
      },
      analyze: {
        step: "02",
        title: "AI Analyzes Deals",
        description: "Machine learning models evaluate each property against market data to find underpriced gems.",
      },
      alert: {
        step: "03",
        title: "You Get Alerted",
        description: "Receive instant notifications when properties matching your criteria appear below market value.",
      },
    },
  },
  footer: {
    cta: {
      title: "Ready to Find Your Next Deal?",
      subtitle: "Join thousands of investors already using Dealigator to discover undervalued properties.",
      emailPlaceholder: "Enter your email",
      button: "Get Started Free",
    },
    brand: {
      description:
        "The smart way to buy a home. AI valuation, legal protection and professional price negotiation. We work only for you, not for the seller.",
    },
    platform: {
      title: "Platform",
      search: "Search Deals",
      analytics: "Analytics",
      alerts: "Price Alerts",
      api: "API Access",
    },
    resources: {
      title: "Resources",
      blog: "Blog",
      guides: "Market Guides",
      support: "Support",
      pricing: "Pricing",
    },
    contact: {
      title: "Contact",
      location: "Sofia, Bulgaria",
      feedback: "Feedback Form",
    },
    legal: {
      rights: "All rights reserved.",
      privacy: "Privacy Policy",
      terms: "Terms of Service",
    },
  },
  contact: {
    back: "Back",
    title: "Contact Us",
    subtitle: "We'd love to hear from you. Send us a message and we'll respond as soon as possible.",
    formTitle: "Send a Message",
    formDescription: "Fill out the form below and we'll get back to you shortly.",
    name: "Name",
    namePlaceholder: "Your name",
    email: "Email",
    emailPlaceholder: "your@email.com",
    subject: "Subject",
    subjectPlaceholder: "How can we help?",
    message: "Message",
    messagePlaceholder: "Tell us more about your inquiry...",
    submit: "Send Message",
    sending: "Sending...",
    successTitle: "Message Sent",
    successDescription: "Thank you for your message. We'll get back to you soon!",
    errors: {
      nameMin: "Name must be at least 2 characters",
      nameMax: "Name must be less than 100 characters",
      invalidEmail: "Please enter a valid email address",
      emailMax: "Email must be less than 255 characters",
      subjectMin: "Subject must be at least 3 characters",
      subjectMax: "Subject must be less than 200 characters",
      messageMin: "Message must be at least 10 characters",
      messageMax: "Message must be less than 2000 characters",
      captchaRequired: "Captcha required",
      captchaDescription: "Please complete the captcha verification",
      submitFailed: "Submission failed",
      tryAgain: "Please try again later",
    },
  },
  auth: {
    backToHome: "Back to Home",
    signIn: "Sign In",
    signUp: "Create Account",
    signInDesc: "Sign in to access your saved filters and alerts",
    signUpDesc: "Register to track the best deals",
    name: "Name",
    namePlaceholder: "Your name",
    email: "Email",
    emailPlaceholder: "email@example.com",
    password: "Password",
    loading: "Loading...",
    or: "or",
    continueWithGoogle: "Continue with Google",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    invalidEmail: "Please enter a valid email",
    passwordMin: "Password must be at least 6 characters",
    nameMin: "Name must be at least 2 characters",
    loginError: "Login Error",
    invalidCredentials: "Invalid email or password",
    error: "Error",
    welcomeBack: "Welcome back!",
    signedIn: "Successfully logged in",
    userExists: "User already exists",
    userExistsDesc: "This email is already registered. Try logging in.",
    registrationError: "Registration Error",
    registrationSuccess: "Registration successful",
    accountCreated: "Account created. Welcome!",
    somethingWrong: "Something went wrong. Please try again.",
    googleError: "Failed to sign in with Google",
    confirmEmail: "Confirm your email",
    confirmEmailSent: "We sent a confirmation link to",
    confirmEmailDesc: "Please check your inbox and click the link to activate your account.",
    checkSpam: "Check your spam folder if you don't see the email.",
    backToLogin: "Back to login",
  },
  dashboard: {
    back: "Back",
    signOut: "Sign Out",
    title: "Dashboard",
    tabs: {
      filters: "Saved Filters",
      alerts: "Alerts",
      profile: "Profile",
    },
    filters: {
      title: "Saved Filters",
      description: "Manage your saved property search filters",
      create: "Create Filter",
      noFilters: "No saved filters",
      noFiltersDesc: "Create your first search filter to start tracking properties.",
      location: "Location",
      priceRange: "Price Range",
      area: "Area",
      type: "Type",
      rooms: "rooms",
      edit: "Edit",
      delete: "Delete",
      filterName: "Filter Name",
      filterNamePlaceholder: "e.g. Sofia Center Apartments",
      locationLabel: "Location",
      locationPlaceholder: "City or neighborhood",
      minPrice: "Min Price (\u20AC)",
      maxPrice: "Max Price (\u20AC)",
      minArea: "Min Area (m\u00B2)",
      maxArea: "Max Area (m\u00B2)",
      propertyType: "Property Type",
      selectType: "Select type",
      apartment: "Apartment",
      house: "House",
      studio: "Studio",
      commercial: "Commercial",
      land: "Land",
      roomsLabel: "Rooms",
      saveFilter: "Save Filter",
      cancel: "Cancel",
    },
    alerts: {
      title: "My Alerts",
      description: "Configure alerts for new deals",
      create: "Create Alert",
      noAlerts: "No alerts configured",
      noAlertsDesc: "Create an alert to get notified about new deals matching your criteria.",
      active: "Active",
      linkedFilter: "Linked filter",
      minScore: "Min deal score",
      maxDeviation: "Max market deviation",
      notifications: "Notifications",
      email: "Email",
      push: "Push",
      delete: "Delete",
      alertName: "Alert Name",
      alertNamePlaceholder: "e.g. Hot deals in Sofia",
      selectFilter: "Linked Filter (optional)",
      noFilter: "No filter",
      minDealScore: "Minimum Deal Score",
      maxPriceDeviation: "Max Price vs Market (%)",
      notifyEmail: "Email Notifications",
      notifyPush: "Push Notifications",
      saveAlert: "Save Alert",
      cancel: "Cancel",
    },
    profile: {
      title: "Profile Settings",
      description: "Manage your account information",
      fullName: "Full Name",
      fullNamePlaceholder: "Your name",
      email: "Email",
      emailPlaceholder: "Your email",
      saveChanges: "Save Changes",
      statistics: "Statistics",
      savedFilters: "Saved Filters",
      activeAlerts: "Active Alerts",
      profileUpdated: "Profile updated",
      profileUpdatedDesc: "Your profile has been successfully updated.",
      updateError: "Error",
      updateErrorDesc: "Failed to update profile.",
    },
    toast: {
      filterCreated: "Filter created",
      filterCreatedDesc: "Your filter has been saved.",
      filterDeleted: "Filter deleted",
      filterDeletedDesc: "The filter has been removed.",
      alertCreated: "Alert created",
      alertCreatedDesc: "You will receive notifications for matching deals.",
      alertDeleted: "Alert deleted",
      alertDeletedDesc: "The alert has been removed.",
      alertUpdated: "Alert updated",
      alertUpdatedDesc: "Alert status has been changed.",
      error: "Error",
      errorDesc: "An error occurred. Please try again.",
    },
  },
  properties: {
    title: "Property Search",
    subtitle: "Find the best deals on the market",
    filtersTitle: "Search Filters",
    searchLabel: "Search by keywords",
    searchPlaceholder: "District, description, address...",
    cityLabel: "City",
    cityPlaceholder: "City",
    allCities: "All cities",
    propertyTypeLabel: "Property Type",
    allTypes: "All types",
    roomsLabel: "Number of Rooms",
    allRooms: "Any",
    roomsSuffix: "rooms",
    areaLabel: "Area (m²)",
    priceLabel: "Price (€)",
    pricePerSqmLabel: "Price per m² (€)",
    from: "From",
    to: "To",
    minPrice: "Min price",
    maxPrice: "Max price",
    minArea: "Min m²",
    maxArea: "Max m²",
    searchButton: "Search",
    reset: "Reset",
    noResults: "No properties found",
    showing: "Showing",
    of: "of",
    sortLabel: "Sort:",
    sortBest: "Best",
    sortNew: "New",
    sortMore: "Sort",
    sortPriceAsc: "Price ↑",
    sortPriceDesc: "Price ↓",
    sortAiValuation: "AI Valuation",
    sortArea: "Area",
    sortPricePerSqm: "Price/m²",
    sortHot: "Hot Deals 🔥",
    sortHotTooltip: "Available only for Apartments",
    advancedFilters: "Advanced Filters",
    advancedActive: "Active",
    floorLabel: "Floor",
    constructionYearLabel: "Construction Year",
    buildingMaterialsLabel: "Building Type",
    buildingMaterialsAll: "All",
    gasLabel: "Gas",
    gasOnly: "Gas only",
    centralHeatingLabel: "Central Heating",
    centralHeatingOnly: "Central heating only",
    selectApartments: "Select type \"Apartments\"",
  },
  propertyDetail: {
    backToSearch: "Back to search",
    notFound: "Property not found",
    description: "Description",
    characteristics: "Characteristics",
    pricePerSqm: "Price/m²",
    area: "Area",
    bedrooms: "Rooms",
    propertyType: "Property Type",
    floor: "Floor",
    construction: "Construction",
    buildingMaterials: {
      brick: "Brick",
      panel: "Panel",
      monolith: "Monolith",
      epk: "EPK",
      other: "Other",
    },
    daysOnMarket: "Days on market",
    today: "Today",
    day: "day",
    days: "days",
    viewOriginal: "View on source",
    save: "Save",
    potentialSavings: "Potential savings",
    listingPrice: "Listing price",
    aiValuation: "AI Valuation",
    priceDeviation: "Price deviation",
    similarProperties: "Similar properties",
    utilityCost: "Utility costs",
    priceComparison: "vs District avg",
    vsAvg: "vs avg",
    priceTrend: "Price trend in district",
    last6Months: "Last 6 months",
    districtAvg: "District avg",
    location: "Location",
    openMaps: "Open in Maps",
    noLocation: "Location not available",
    contact: "Contact",
    ownerContacts: "Owner Contacts",
    name: "Name",
    phone: "Phone",
    email: "Email",
    district: "District",
    noContactInfo: "No contact information available",
    moreDetails: "More details",
    districtRatings: {
      traffic: "Traffic",
      publicTransport: "Public Transport",
      shops: "Shops",
      schools: "Schools",
    },
  },
  propertyCard: {
    aiEstimate: "AI Est",
    goodDeal: "Good Deal",
    overpriced: "Overpriced",
    belowMarket: "below market",
    aboveMarket: "above market",
    fairPrice: "fair price",
    significantlyBelow: "Significantly below market price",
    significantlyAbove: "Significantly above market price",
    rooms: "rooms",
    types: {
      apartments: "Apartment",
      house: "House",
      studio: "Studio",
      garage: "Garage",
      land: "Land",
      commercial: "Commercial",
      other: "Property",
    },
  },
  servicePage: {
    pageTitle: "Services",
    backToServices: "Back to services",
    details: "Details",
    order: "Order",
    consultation: "Consultation",
    addToCart: "Add to cart",
    alreadyInCart: "Already in cart",
    addedToCart: "Added to cart",
    mostPopular: "Most Popular",
    cart: "Cart",
    cartEmpty: "Your cart is empty",
    browseServices: "Browse services",
    name: "Name *",
    email: "Email *",
    phone: "Phone",
    notes: "Notes",
    paymentNote: "* Payment is made after discussing the details with our team.",
    confirmOrder: "Confirm order",
    cancel: "Cancel",
    orderAccepted: "Order accepted!",
    orderAcceptedDesc: "We will contact you by email as soon as possible to discuss the details.",
    ok: "OK",
    fillNameEmail: "Please fill in name and email",
    orderError: "Error placing order",
    waitSecurity: "Please wait for the security check",
    cartOrderNote: "Order from cart",
    consultationBtn: "Service Consultation",
    consultationTitle: "Service Consultation",
    consultationName: "Name *",
    consultationEmail: "Email *",
    consultationPhone: "Phone",
    consultationMessage: "Your question *",
    consultationSubmit: "Send",
    consultationSuccess: "Success!",
    consultationSuccessDesc: "We will contact you soon.",
    consultationError: "Error",
    consultationErrorDesc: "Please try again.",
  },
};

const bg = {
  nav: {
    home: "Начало",
    deals: "Търсене",
    analytics: "\u0410\u043D\u0430\u043B\u0438\u0437\u0438",
    howItWorks: "\u041A\u0430\u043A \u0440\u0430\u0431\u043E\u0442\u0438",
    signIn: "\u0412\u0445\u043E\u0434",
    getStarted: "\u0417\u0430\u043F\u043E\u0447\u043D\u0435\u0442\u0435",
    dashboard: "\u0422\u0430\u0431\u043B\u043E",
    myFilters: "\u041C\u043E\u0438\u0442\u0435 \u0444\u0438\u043B\u0442\u0440\u0438",
    myAlerts: "\u041C\u043E\u0438\u0442\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
    signOut: "Изход",
    favorites: "Избрани",
  },
  hero: {
    title: "Купувате имот? Ние сме на Ваша страна.",
    subtitle:
      "Единствената услуга в България, която съчетава AI оценка с 97% точност и професионални преговарящи лица, за да Ви спести хиляди евро и нерви.",
    ctaPrimary: "Намери и оцени имот",
    ctaSecondary: "Виж услугите",
    stats: {
      properties: "Анализирани имоти",
      avgSavings: "Средно спестени пари на една сделка благодарение на нашия алгоритъм",
      activeAlerts: "Активни известия",
      citiesCovered: "Покрити градове",
    },
    banners: {
      banner1Title: "Страхувате се, че ще надплатите за апартамент в България?",
      banner1Description:
        "Безплатно ще изчислим колко всъщност струва квадратен метър в избрания от Вас район или град",
      banner1Bonus: "Също така ще получите отстъпка -20% на всяка наша услуга",
      banner1Cta: "Изчисли",
      banner2Title: "Инвестирате в българска недвижимост?",
      banner2Description:
        "Безплатно получете закрита аналитика: топлинна карта на стойността на м² и реален годишен прираст на цените (ROI) по райони на София или крайбрежието",
      banner2Bonus: "Също така ще получите отстъпка -20% на всяка наша услуга",
      banner2Cta: "Получи аналитика",
    },
  },
  marketProblem: {
    title: "Пазарът работи срещу купувача. Ние променяме правилата.",
    description:
      "Обикновените брокери защитават продавача или комисионната си. Dealigator работи само за Вас — ние сваляме цената и гарантираме чистотата на сделката.",
    standard: {
      title: "Стандарт",
      seller: "Продавач",
      sellerBroker: "Брокер на продавача",
      wantHighPrice: "И двамата искат по-висока цена",
      vs: "VS",
      lonelyBuyer: "Самостоятелен купувач",
    },
    dealigator: {
      title: "С Dealigator",
      buyer: "Купувач",
      aiLawyerNegotiator: "AI + Юрист + Преговарящо лице",
      fullProtection: "Пълна защита на вашите интереси",
      vs: "VS",
      seller: "Продавач",
    },
  },
  secretWeapon: {
    title: "Знаем реалната цена преди преговорите.",
    description: "Нашият алгоритъм анализира над 50 фактора и оценява имота с точност 97%.",
    benefit:
      'Как ще Ви помогне това? Вие отивате на оглед с "асо в ръкава" — знаете реалната стойност на жилището и имате аргументи да свалите цената',
  },
  services: {
    title: "Пълна защита от търсенето до ключа",
    aiAnalysis: {
      title: "AI Оценка & Анализ",
      description:
        "Намираме подценени имоти и показваме реалната им стойност, скритите рискове и потенциала за отдаване под наем.",
    },
    negotiation: {
      title: "Преговори & Сделка",
      description:
        "Наш експерт преговаря вместо Вас. Целта: да свалим цената с 10-15%. Ако не свалим цената — услугата за преговори е безплатна.",
    },
    legal: {
      title: "Правна сигурност",
      description: "Проверяваме имота за тежести, подготвяме договорите и защитаваме парите Ви в доверителна сметка.",
    },
  },
  battle: {
    title: "Защо да изберете Dealigator?",
    broker: "Обикновен брокер",
    dealigator: "Dealigator",
    interest: { label: "Чий интерес защитава?", broker: "На продавача / Своя", ai: "Само Вашия (на Купувача)" },
    priceEval: {
      label: "Оценка на цената",
      broker: "По преценка на продавача",
      ai: "AI Алгоритъм (97% точност)",
    },
    goalNeg: { label: "Цел на преговорите", broker: "Бърза сделка", ai: "Максимална отстъпка" },
    legalCheck: { label: "Правна проверка", broker: "Повърхностна", ai: "Дълбока (Тежести, история)" },
    pricing: { label: "Цена", broker: "3-6% комисионна", ai: "2.5% или фиксирана такса (в зависимост от услугата)" },
  },
  featuredDeals: {
    badge: "Ново",
    title: "Последни обяви",
    subtitle: "Най-новите апартаменти на пазара — бъдете първи, които ги виждат.",
    viewAll: "Виж всички",
    belowMarket: "под пазарната",
    beds: "спални",
    baths: "бани",
    viewDeal: "Виж оферта",
  },
  analyticsPreview: {
    badge: "\u0423\u043C\u043D\u0438 \u0430\u043D\u0430\u043B\u0438\u0437\u0438",
    title:
      "\u0412\u0430\u0448\u0430\u0442\u0430 \u043B\u0438\u0447\u043D\u0430 \u0430\u043D\u0430\u043B\u0438\u0442\u0438\u0447\u043D\u0430 \u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0430",
    subtitle:
      "\u0417\u0430\u0434\u044A\u043B\u0431\u043E\u0447\u0435\u043D\u0438 \u043F\u0430\u0437\u0430\u0440\u043D\u0438 \u0430\u043D\u0430\u043B\u0438\u0437\u0438, \u043F\u0440\u043E\u0433\u043D\u043E\u0437\u0438 \u0437\u0430 \u0446\u0435\u043D\u0438 \u0438 \u0430\u043D\u0430\u043B\u0438\u0437 \u043D\u0430 \u0442\u0435\u043D\u0434\u0435\u043D\u0446\u0438\u0438 \u0437\u0430 \u0438\u043D\u0444\u043E\u0440\u043C\u0438\u0440\u0430\u043D\u0438 \u0438\u043D\u0432\u0435\u0441\u0442\u0438\u0446\u0438\u043E\u043D\u043D\u0438 \u0440\u0435\u0448\u0435\u043D\u0438\u044F.",
    features: {
      priceAnalytics: {
        title: "\u0426\u0435\u043D\u043E\u0432\u0438 \u0430\u043D\u0430\u043B\u0438\u0437\u0438",
        description:
          "\u0418\u0441\u0442\u043E\u0440\u0438\u0447\u0435\u0441\u043A\u0438 \u0442\u0435\u043D\u0434\u0435\u043D\u0446\u0438\u0438 \u0438 \u043F\u0440\u043E\u0433\u043D\u043E\u0437\u0438",
      },
      heatMaps: {
        title: "\u0422\u043E\u043F\u043B\u0438\u043D\u043D\u0438 \u043A\u0430\u0440\u0442\u0438",
        description:
          "\u0412\u0438\u0437\u0443\u0430\u043B\u0438\u0437\u0438\u0440\u0430\u0439\u0442\u0435 \u0433\u043E\u0440\u0435\u0449\u0438\u0442\u0435 \u0437\u043E\u043D\u0438",
      },
      roiCalculator: {
        title: "ROI \u043A\u0430\u043B\u043A\u0443\u043B\u0430\u0442\u043E\u0440",
        description:
          "\u041E\u0446\u0435\u043D\u0435\u0442\u0435 \u0432\u044A\u0437\u0432\u0440\u044A\u0449\u0430\u0435\u043C\u043E\u0441\u0442\u0442\u0430",
      },
      dealAlerts: {
        title: "\u0418\u0437\u0432\u0435\u0441\u0442\u0438\u044F \u0437\u0430 \u0441\u0434\u0435\u043B\u043A\u0438",
        description:
          "\u041C\u043E\u043C\u0435\u043D\u0442\u0430\u043B\u043D\u0438 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F \u0437\u0430 \u043F\u043E\u0434\u0445\u043E\u0434\u044F\u0449\u0438 \u0441\u0434\u0435\u043B\u043A\u0438",
      },
    },
    exploreButton:
      "\u0420\u0430\u0437\u0433\u043B\u0435\u0434\u0430\u0439 \u0430\u043D\u0430\u043B\u0438\u0437\u0438\u0442\u0435",
    marketOverview:
      "\u041F\u0440\u0435\u0433\u043B\u0435\u0434 \u043D\u0430 \u043F\u0430\u0437\u0430\u0440\u0430 \u0432 \u0421\u043E\u0444\u0438\u044F",
    pricePerSqm: "\u0426\u0435\u043D\u0430/\u043A\u0432.\u043C",
    listings: "\u041E\u0431\u044F\u0432\u0438",
    avgDays: "\u0421\u0440\u0435\u0434\u043D\u043E \u0434\u043D\u0438",
    newDealFound: "\u041D\u043E\u0432\u0430 \u0441\u0434\u0435\u043B\u043A\u0430!",
    matchesFilters:
      "\u0421\u044A\u0432\u043F\u0430\u0434\u0430 \u0441 \u0444\u0438\u043B\u0442\u0440\u0438\u0442\u0435 \u0432\u0438",
  },
  howItWorks: {
    badge: "\u041F\u0440\u043E\u0441\u0442 \u043F\u0440\u043E\u0446\u0435\u0441",
    title: "\u041A\u0430\u043A \u0440\u0430\u0431\u043E\u0442\u0438 Dealigator",
    subtitle:
      "\u0422\u0440\u0438 \u043B\u0435\u0441\u043D\u0438 \u0441\u0442\u044A\u043F\u043A\u0438 \u0434\u043E \u043F\u0435\u0440\u0444\u0435\u043A\u0442\u043D\u0430\u0442\u0430 \u0438\u043C\u043E\u0442\u043D\u0430 \u0441\u0434\u0435\u043B\u043A\u0430.",
    steps: {
      collect: {
        step: "01",
        title: "\u0421\u044A\u0431\u0438\u0440\u0430\u043C\u0435 \u0434\u0430\u043D\u043D\u0438",
        description:
          "\u041D\u0430\u0448\u0430\u0442\u0430 \u0441\u0438\u0441\u0442\u0435\u043C\u0430 \u0441\u044A\u0431\u0438\u0440\u0430 \u043E\u0431\u044F\u0432\u0438 \u043E\u0442 20+ \u0438\u0437\u0442\u043E\u0447\u043D\u0438\u043A\u0430 \u0432 \u0411\u044A\u043B\u0433\u0430\u0440\u0438\u044F, \u0430\u043A\u0442\u0443\u0430\u043B\u0438\u0437\u0438\u0440\u0430\u0439\u043A\u0438 \u0432\u0441\u0435\u043A\u0438 \u0447\u0430\u0441.",
      },
      analyze: {
        step: "02",
        title: "AI \u0430\u043D\u0430\u043B\u0438\u0437\u0438\u0440\u0430",
        description:
          "\u041C\u0430\u0448\u0438\u043D\u043D\u043E \u043E\u0431\u0443\u0447\u0435\u043D\u0438\u0435 \u043E\u0446\u0435\u043D\u044F\u0432\u0430 \u0432\u0441\u0435\u043A\u0438 \u0438\u043C\u043E\u0442 \u0441\u043F\u0440\u044F\u043C\u043E \u043F\u0430\u0437\u0430\u0440\u043D\u0438 \u0434\u0430\u043D\u043D\u0438 \u0437\u0430 \u043F\u043E\u0434\u0446\u0435\u043D\u0435\u043D\u0438 \u043D\u0430\u0445\u043E\u0434\u043A\u0438.",
      },
      alert: {
        step: "03",
        title:
          "\u041F\u043E\u043B\u0443\u0447\u0430\u0432\u0430\u0442\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u0435",
        description:
          "\u041F\u043E\u043B\u0443\u0447\u0430\u0432\u0430\u0439\u0442\u0435 \u043C\u043E\u043C\u0435\u043D\u0442\u0430\u043B\u043D\u0438 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F \u043A\u043E\u0433\u0430\u0442\u043E \u0438\u043C\u043E\u0442\u0438 \u043F\u043E \u0432\u0430\u0448\u0438\u0442\u0435 \u043A\u0440\u0438\u0442\u0435\u0440\u0438\u0438 \u0441\u0435 \u043F\u043E\u044F\u0432\u044F\u0442 \u043F\u043E\u0434 \u043F\u0430\u0437\u0430\u0440\u043D\u0430\u0442\u0430 \u0441\u0442\u043E\u0439\u043D\u043E\u0441\u0442.",
      },
    },
  },
  footer: {
    cta: {
      title:
        "\u0413\u043E\u0442\u043E\u0432\u0438 \u0437\u0430 \u0441\u043B\u0435\u0434\u0432\u0430\u0449\u0430\u0442\u0430 \u0441\u0434\u0435\u043B\u043A\u0430?",
      subtitle:
        "\u041F\u0440\u0438\u0441\u044A\u0435\u0434\u0438\u043D\u0435\u0442\u0435 \u0441\u0435 \u043A\u044A\u043C \u0445\u0438\u043B\u044F\u0434\u0438 \u0438\u043D\u0432\u0435\u0441\u0442\u0438\u0442\u043E\u0440\u0438, \u043A\u043E\u0438\u0442\u043E \u0432\u0435\u0447\u0435 \u0438\u0437\u043F\u043E\u043B\u0437\u0432\u0430\u0442 Dealigator \u0437\u0430 \u043E\u0442\u043A\u0440\u0438\u0432\u0430\u043D\u0435 \u043D\u0430 \u043F\u043E\u0434\u0446\u0435\u043D\u0435\u043D\u0438 \u0438\u043C\u043E\u0442\u0438.",
      emailPlaceholder: "\u0412\u044A\u0432\u0435\u0434\u0435\u0442\u0435 \u0438\u043C\u0435\u0439\u043B",
      button:
        "\u0417\u0430\u043F\u043E\u0447\u043D\u0435\u0442\u0435 \u0431\u0435\u0437\u043F\u043B\u0430\u0442\u043D\u043E",
    },
    brand: {
      description:
        "Интелигентният начин да купите дом. AI оценка, юридическа защита и професионално договаряне на цена. Ние работим само за Вас, не за продавача.",
    },
    platform: {
      title: "\u041F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0430",
      search: "\u0422\u044A\u0440\u0441\u0435\u043D\u0435 \u043D\u0430 \u043E\u0444\u0435\u0440\u0442\u0438",
      analytics: "\u0410\u043D\u0430\u043B\u0438\u0437\u0438",
      alerts: "\u0426\u0435\u043D\u043E\u0432\u0438 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      api: "API \u0434\u043E\u0441\u0442\u044A\u043F",
    },
    resources: {
      title: "\u0420\u0435\u0441\u0443\u0440\u0441\u0438",
      blog: "\u0411\u043B\u043E\u0433",
      guides:
        "\u041F\u0430\u0437\u0430\u0440\u043D\u0438 \u0440\u044A\u043A\u043E\u0432\u043E\u0434\u0441\u0442\u0432\u0430",
      support: "\u041F\u043E\u0434\u0434\u0440\u044A\u0436\u043A\u0430",
      pricing: "\u0426\u0435\u043D\u0438",
    },
    contact: {
      title: "Контакти",
      location: "\u0421\u043E\u0444\u0438\u044F, \u0411\u044A\u043B\u0433\u0430\u0440\u0438\u044F",
      feedback: "Форма за обратна връзка",
    },
    legal: {
      rights:
        "\u0412\u0441\u0438\u0447\u043A\u0438 \u043F\u0440\u0430\u0432\u0430 \u0437\u0430\u043F\u0430\u0437\u0435\u043D\u0438.",
      privacy:
        "\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u0437\u0430 \u043F\u043E\u0432\u0435\u0440\u0438\u0442\u0435\u043B\u043D\u043E\u0441\u0442",
      terms: "\u0423\u0441\u043B\u043E\u0432\u0438\u044F \u0437\u0430 \u043F\u043E\u043B\u0437\u0432\u0430\u043D\u0435",
    },
  },
  contact: {
    back: "Назад",
    title: "Свържете се с нас",
    subtitle: "Ще се радваме да ви чуем. Изпратете ни съобщение и ще отговорим възможно най-скоро.",
    formTitle: "Изпратете съобщение",
    formDescription: "Попълнете формуляра по-долу и ще се свържем с вас скоро.",
    name: "Име",
    namePlaceholder: "Вашето име",
    email: "Имейл",
    emailPlaceholder: "your@email.com",
    subject: "Тема",
    subjectPlaceholder: "Как можем да помогнем?",
    message: "Съобщение",
    messagePlaceholder: "Разкажете ни повече за вашето запитване...",
    submit: "Изпрати съобщение",
    sending: "Изпращане...",
    successTitle: "Съобщението е изпратено",
    successDescription: "Благодарим ви за съобщението. Ще се свържем с вас скоро!",
    errors: {
      nameMin: "Името трябва да е поне 2 символа",
      nameMax: "Името трябва да е по-малко от 100 символа",
      invalidEmail: "Моля, въведете валиден имейл адрес",
      emailMax: "Имейлът трябва да е по-малко от 255 символа",
      subjectMin: "Темата трябва да е поне 3 символа",
      subjectMax: "Темата трябва да е по-малка от 200 символа",
      messageMin: "Съобщението трябва да е поне 10 символа",
      messageMax: "Съобщението трябва да е по-малко от 2000 символа",
      captchaRequired: "Изисква се captcha",
      captchaDescription: "Моля, попълнете captcha проверката",
      submitFailed: "Грешка при изпращане",
      tryAgain: "Моля, опитайте отново по-късно",
    },
  },
  auth: {
    backToHome: "\u041A\u044A\u043C \u043D\u0430\u0447\u0430\u043B\u043E\u0442\u043E",
    signIn: "\u0412\u0445\u043E\u0434",
    signUp: "\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F",
    signInDesc:
      "\u0412\u043B\u0435\u0437\u0442\u0435 \u0437\u0430 \u0434\u043E\u0441\u0442\u044A\u043F \u0434\u043E \u0437\u0430\u043F\u0430\u0437\u0435\u043D\u0438\u0442\u0435 \u0444\u0438\u043B\u0442\u0440\u0438 \u0438 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
    signUpDesc:
      "\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u0430\u0439\u0442\u0435 \u0441\u0435 \u0437\u0430 \u043F\u0440\u043E\u0441\u043B\u0435\u0434\u044F\u0432\u0430\u043D\u0435 \u043D\u0430 \u043D\u0430\u0439-\u0434\u043E\u0431\u0440\u0438\u0442\u0435 \u0441\u0434\u0435\u043B\u043A\u0438",
    name: "\u0418\u043C\u0435",
    namePlaceholder: "\u0412\u0430\u0448\u0435\u0442\u043E \u0438\u043C\u0435",
    email: "\u0418\u043C\u0435\u0439\u043B",
    emailPlaceholder: "email@example.com",
    password: "\u041F\u0430\u0440\u043E\u043B\u0430",
    loading: "\u0417\u0430\u0440\u0435\u0436\u0434\u0430\u043D\u0435...",
    or: "\u0438\u043B\u0438",
    continueWithGoogle: "\u041F\u0440\u043E\u0434\u044A\u043B\u0436\u0438 \u0441 Google",
    noAccount: "\u041D\u044F\u043C\u0430\u0442\u0435 \u0430\u043A\u0430\u0443\u043D\u0442?",
    haveAccount: "\u0412\u0435\u0447\u0435 \u0438\u043C\u0430\u0442\u0435 \u0430\u043A\u0430\u0443\u043D\u0442?",
    invalidEmail:
      "\u041C\u043E\u043B\u044F \u0432\u044A\u0432\u0435\u0434\u0435\u0442\u0435 \u0432\u0430\u043B\u0438\u0434\u0435\u043D \u0438\u043C\u0435\u0439\u043B",
    passwordMin:
      "\u041F\u0430\u0440\u043E\u043B\u0430\u0442\u0430 \u0442\u0440\u044F\u0431\u0432\u0430 \u0434\u0430 \u0435 \u043F\u043E\u043D\u0435 6 \u0441\u0438\u043C\u0432\u043E\u043B\u0430",
    nameMin:
      "\u0418\u043C\u0435\u0442\u043E \u0442\u0440\u044F\u0431\u0432\u0430 \u0434\u0430 \u0435 \u043F\u043E\u043D\u0435 2 \u0441\u0438\u043C\u0432\u043E\u043B\u0430",
    loginError: "\u0413\u0440\u0435\u0448\u043A\u0430 \u043F\u0440\u0438 \u0432\u0445\u043E\u0434",
    invalidCredentials:
      "\u041D\u0435\u0432\u0430\u043B\u0438\u0434\u0435\u043D \u0438\u043C\u0435\u0439\u043B \u0438\u043B\u0438 \u043F\u0430\u0440\u043E\u043B\u0430",
    error: "\u0413\u0440\u0435\u0448\u043A\u0430",
    welcomeBack: "\u0414\u043E\u0431\u0440\u0435 \u0434\u043E\u0448\u043B\u0438!",
    signedIn: "\u0423\u0441\u043F\u0435\u0448\u0435\u043D \u0432\u0445\u043E\u0434",
    userExists:
      "\u041F\u043E\u0442\u0440\u0435\u0431\u0438\u0442\u0435\u043B\u044F\u0442 \u0441\u044A\u0449\u0435\u0441\u0442\u0432\u0443\u0432\u0430",
    userExistsDesc:
      "\u0422\u043E\u0437\u0438 \u0438\u043C\u0435\u0439\u043B \u0432\u0435\u0447\u0435 \u0435 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u0430\u043D. \u041E\u043F\u0438\u0442\u0430\u0439\u0442\u0435 \u0432\u0445\u043E\u0434.",
    registrationError:
      "\u0413\u0440\u0435\u0448\u043A\u0430 \u043F\u0440\u0438 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F",
    registrationSuccess:
      "\u0423\u0441\u043F\u0435\u0448\u043D\u0430 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F",
    accountCreated:
      "\u0410\u043A\u0430\u0443\u043D\u0442\u044A\u0442 \u0435 \u0441\u044A\u0437\u0434\u0430\u0434\u0435\u043D. \u0414\u043E\u0431\u0440\u0435 \u0434\u043E\u0448\u043B\u0438!",
    somethingWrong:
      "\u041D\u0435\u0449\u043E \u0441\u0435 \u043E\u0431\u044A\u0440\u043A\u0430. \u041C\u043E\u043B\u044F \u043E\u043F\u0438\u0442\u0430\u0439\u0442\u0435 \u043E\u0442\u043D\u043E\u0432\u043E.",
    googleError: "\u041D\u0435\u0443\u0441\u043F\u0435\u0448\u0435\u043D \u0432\u0445\u043E\u0434 \u0441 Google",
    confirmEmail: "Потвърдете вашия имейл",
    confirmEmailSent: "Изпратихме линк за потвърждение на",
    confirmEmailDesc: "Моля проверете входящата си поща и кликнете върху линка, за да активирате акаунта си.",
    checkSpam: "Проверете папката за спам, ако не виждате имейла.",
    backToLogin: "Обратно към входа",
  },
  dashboard: {
    back: "\u041D\u0430\u0437\u0430\u0434",
    signOut: "\u0418\u0437\u0445\u043E\u0434",
    title: "\u0422\u0430\u0431\u043B\u043E",
    tabs: {
      filters: "\u0417\u0430\u043F\u0430\u0437\u0435\u043D\u0438 \u0444\u0438\u043B\u0442\u0440\u0438",
      alerts: "\u0418\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      profile: "\u041F\u0440\u043E\u0444\u0438\u043B",
    },
    filters: {
      title: "\u0417\u0430\u043F\u0430\u0437\u0435\u043D\u0438 \u0444\u0438\u043B\u0442\u0440\u0438",
      description:
        "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0432\u0430\u0439\u0442\u0435 \u0437\u0430\u043F\u0430\u0437\u0435\u043D\u0438\u0442\u0435 \u0444\u0438\u043B\u0442\u0440\u0438 \u0437\u0430 \u0442\u044A\u0440\u0441\u0435\u043D\u0435",
      create: "\u0421\u044A\u0437\u0434\u0430\u0439 \u0444\u0438\u043B\u0442\u044A\u0440",
      noFilters:
        "\u041D\u044F\u043C\u0430 \u0437\u0430\u043F\u0430\u0437\u0435\u043D\u0438 \u0444\u0438\u043B\u0442\u0440\u0438",
      noFiltersDesc:
        "\u0421\u044A\u0437\u0434\u0430\u0439\u0442\u0435 \u043F\u044A\u0440\u0432\u0438\u044F \u0441\u0438 \u0444\u0438\u043B\u0442\u044A\u0440 \u0437\u0430 \u043F\u0440\u043E\u0441\u043B\u0435\u0434\u044F\u0432\u0430\u043D\u0435 \u043D\u0430 \u0438\u043C\u043E\u0442\u0438.",
      location: "\u041B\u043E\u043A\u0430\u0446\u0438\u044F",
      priceRange: "\u0426\u0435\u043D\u043E\u0432\u0438 \u0434\u0438\u0430\u043F\u0430\u0437\u043E\u043D",
      area: "\u041F\u043B\u043E\u0449\u0430\u0434",
      type: "\u0422\u0438\u043F",
      rooms: "\u0441\u0442\u0430\u0438",
      edit: "\u0420\u0435\u0434\u0430\u043A\u0442\u0438\u0440\u0430\u0439",
      delete: "\u0418\u0437\u0442\u0440\u0438\u0439",
      filterName: "\u0418\u043C\u0435 \u043D\u0430 \u0444\u0438\u043B\u0442\u044A\u0440\u0430",
      filterNamePlaceholder:
        "\u043D\u0430\u043F\u0440. \u0410\u043F\u0430\u0440\u0442\u0430\u043C\u0435\u043D\u0442\u0438 \u0446\u0435\u043D\u0442\u044A\u0440 \u0421\u043E\u0444\u0438\u044F",
      locationLabel: "\u041B\u043E\u043A\u0430\u0446\u0438\u044F",
      locationPlaceholder: "\u0413\u0440\u0430\u0434 \u0438\u043B\u0438 \u043A\u0432\u0430\u0440\u0442\u0430\u043B",
      minPrice: "\u041C\u0438\u043D \u0446\u0435\u043D\u0430 (\u20AC)",
      maxPrice: "\u041C\u0430\u043A\u0441 \u0446\u0435\u043D\u0430 (\u20AC)",
      minArea: "\u041C\u0438\u043D \u043F\u043B\u043E\u0449\u0430\u0434 (\u043C\u00B2)",
      maxArea: "\u041C\u0430\u043A\u0441 \u043F\u043B\u043E\u0449\u0430\u0434 (\u043C\u00B2)",
      propertyType: "\u0422\u0438\u043F \u0438\u043C\u043E\u0442",
      selectType: "\u0418\u0437\u0431\u0435\u0440\u0435\u0442\u0435 \u0442\u0438\u043F",
      apartment: "\u0410\u043F\u0430\u0440\u0442\u0430\u043C\u0435\u043D\u0442",
      house: "\u041A\u044A\u0449\u0430",
      studio: "\u0421\u0442\u0443\u0434\u0438\u043E",
      commercial: "\u0422\u044A\u0440\u0433\u043E\u0432\u0441\u043A\u0438",
      land: "\u041F\u0430\u0440\u0446\u0435\u043B",
      roomsLabel: "\u0421\u0442\u0430\u0438",
      saveFilter: "\u0417\u0430\u043F\u0430\u0437\u0438 \u0444\u0438\u043B\u0442\u044A\u0440",
      cancel: "\u041E\u0442\u043A\u0430\u0437",
    },
    alerts: {
      title: "\u041C\u043E\u0438\u0442\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      description:
        "\u041A\u043E\u043D\u0444\u0438\u0433\u0443\u0440\u0438\u0440\u0430\u0439\u0442\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F \u0437\u0430 \u043D\u043E\u0432\u0438 \u0441\u0434\u0435\u043B\u043A\u0438",
      create: "\u0421\u044A\u0437\u0434\u0430\u0439 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u0435",
      noAlerts:
        "\u041D\u044F\u043C\u0430 \u043A\u043E\u043D\u0444\u0438\u0433\u0443\u0440\u0438\u0440\u0430\u043D\u0438 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      noAlertsDesc:
        "\u0421\u044A\u0437\u0434\u0430\u0439\u0442\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u0435 \u0437\u0430 \u043D\u043E\u0432\u0438 \u0441\u0434\u0435\u043B\u043A\u0438 \u043F\u043E \u0432\u0430\u0448\u0438\u0442\u0435 \u043A\u0440\u0438\u0442\u0435\u0440\u0438\u0438.",
      active: "\u0410\u043A\u0442\u0438\u0432\u043D\u043E",
      linkedFilter: "\u0421\u0432\u044A\u0440\u0437\u0430\u043D \u0444\u0438\u043B\u0442\u044A\u0440",
      minScore: "\u041C\u0438\u043D \u0441\u043A\u043E\u0440 \u043D\u0430 \u0441\u0434\u0435\u043B\u043A\u0430",
      maxDeviation:
        "\u041C\u0430\u043A\u0441 \u043F\u0430\u0437\u0430\u0440\u043D\u043E \u043E\u0442\u043A\u043B\u043E\u043D\u0435\u043D\u0438\u0435",
      notifications: "\u0418\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      email: "\u0418\u043C\u0435\u0439\u043B",
      push: "Push",
      delete: "\u0418\u0437\u0442\u0440\u0438\u0439",
      alertName: "\u0418\u043C\u0435 \u043D\u0430 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u0435",
      alertNamePlaceholder:
        "\u043D\u0430\u043F\u0440. \u0413\u043E\u0440\u0435\u0449\u0438 \u0441\u0434\u0435\u043B\u043A\u0438 \u0432 \u0421\u043E\u0444\u0438\u044F",
      selectFilter:
        "\u0421\u0432\u044A\u0440\u0437\u0430\u043D \u0444\u0438\u043B\u0442\u044A\u0440 (\u043E\u043F\u0446\u0438\u043E\u043D\u0430\u043B\u043D\u043E)",
      noFilter: "\u0411\u0435\u0437 \u0444\u0438\u043B\u0442\u044A\u0440",
      minDealScore:
        "\u041C\u0438\u043D\u0438\u043C\u0430\u043B\u0435\u043D \u0441\u043A\u043E\u0440 \u043D\u0430 \u0441\u0434\u0435\u043B\u043A\u0430",
      maxPriceDeviation:
        "\u041C\u0430\u043A\u0441 \u0446\u0435\u043D\u0430 \u0441\u043F\u0440\u044F\u043C\u043E \u043F\u0430\u0437\u0430\u0440 (%)",
      notifyEmail: "\u0418\u043C\u0435\u0439\u043B \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      notifyPush: "Push \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      saveAlert: "\u0417\u0430\u043F\u0430\u0437\u0438 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u0435",
      cancel: "\u041E\u0442\u043A\u0430\u0437",
    },
    profile: {
      title:
        "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043D\u0430 \u043F\u0440\u043E\u0444\u0438\u043B\u0430",
      description:
        "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0432\u0430\u0439\u0442\u0435 \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u044F\u0442\u0430 \u0437\u0430 \u0430\u043A\u0430\u0443\u043D\u0442\u0430",
      fullName: "\u041F\u044A\u043B\u043D\u043E \u0438\u043C\u0435",
      fullNamePlaceholder: "\u0412\u0430\u0448\u0435\u0442\u043E \u0438\u043C\u0435",
      email: "\u0418\u043C\u0435\u0439\u043B",
      emailPlaceholder: "\u0412\u0430\u0448\u0438\u044F\u0442 \u0438\u043C\u0435\u0439\u043B",
      saveChanges: "\u0417\u0430\u043F\u0430\u0437\u0438 \u043F\u0440\u043E\u043C\u0435\u043D\u0438\u0442\u0435",
      statistics: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430",
      savedFilters: "\u0417\u0430\u043F\u0430\u0437\u0435\u043D\u0438 \u0444\u0438\u043B\u0442\u0440\u0438",
      activeAlerts: "\u0410\u043A\u0442\u0438\u0432\u043D\u0438 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F",
      profileUpdated:
        "\u041F\u0440\u043E\u0444\u0438\u043B\u044A\u0442 \u0435 \u043E\u0431\u043D\u043E\u0432\u0435\u043D",
      profileUpdatedDesc:
        "\u041F\u0440\u043E\u0444\u0438\u043B\u044A\u0442 \u0432\u0438 \u0431\u0435\u0448\u0435 \u0443\u0441\u043F\u0435\u0448\u043D\u043E \u043E\u0431\u043D\u043E\u0432\u0435\u043D.",
      updateError: "\u0413\u0440\u0435\u0448\u043A\u0430",
      updateErrorDesc:
        "\u041D\u0435\u0443\u0441\u043F\u0435\u0448\u043D\u043E \u043E\u0431\u043D\u043E\u0432\u044F\u0432\u0430\u043D\u0435 \u043D\u0430 \u043F\u0440\u043E\u0444\u0438\u043B\u0430.",
    },
    toast: {
      filterCreated:
        "\u0424\u0438\u043B\u0442\u044A\u0440\u044A\u0442 \u0435 \u0441\u044A\u0437\u0434\u0430\u0434\u0435\u043D",
      filterCreatedDesc:
        "\u0412\u0430\u0448\u0438\u044F\u0442 \u0444\u0438\u043B\u0442\u044A\u0440 \u0435 \u0437\u0430\u043F\u0430\u0437\u0435\u043D.",
      filterDeleted: "\u0424\u0438\u043B\u0442\u044A\u0440\u044A\u0442 \u0435 \u0438\u0437\u0442\u0440\u0438\u0442",
      filterDeletedDesc:
        "\u0424\u0438\u043B\u0442\u044A\u0440\u044A\u0442 \u0431\u0435\u0448\u0435 \u043F\u0440\u0435\u043C\u0430\u0445\u043D\u0430\u0442.",
      alertCreated:
        "\u0418\u0437\u0432\u0435\u0441\u0442\u0438\u0435\u0442\u043E \u0435 \u0441\u044A\u0437\u0434\u0430\u0434\u0435\u043D\u043E",
      alertCreatedDesc:
        "\u0429\u0435 \u043F\u043E\u043B\u0443\u0447\u0430\u0432\u0430\u0442\u0435 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u044F \u0437\u0430 \u0441\u044A\u0432\u043F\u0430\u0434\u0430\u0449\u0438 \u0441\u0434\u0435\u043B\u043A\u0438.",
      alertDeleted:
        "\u0418\u0437\u0432\u0435\u0441\u0442\u0438\u0435\u0442\u043E \u0435 \u0438\u0437\u0442\u0440\u0438\u0442\u043E",
      alertDeletedDesc:
        "\u0418\u0437\u0432\u0435\u0441\u0442\u0438\u0435\u0442\u043E \u0431\u0435\u0448\u0435 \u043F\u0440\u0435\u043C\u0430\u0445\u043D\u0430\u0442\u043E.",
      alertUpdated:
        "\u0418\u0437\u0432\u0435\u0441\u0442\u0438\u0435\u0442\u043E \u0435 \u043E\u0431\u043D\u043E\u0432\u0435\u043D\u043E",
      alertUpdatedDesc:
        "\u0421\u0442\u0430\u0442\u0443\u0441\u044A\u0442 \u043D\u0430 \u0438\u0437\u0432\u0435\u0441\u0442\u0438\u0435\u0442\u043E \u0435 \u043F\u0440\u043E\u043C\u0435\u043D\u0435\u043D.",
      error: "\u0413\u0440\u0435\u0448\u043A\u0430",
      errorDesc:
        "\u0412\u044A\u0437\u043D\u0438\u043A\u043D\u0430 \u0433\u0440\u0435\u0448\u043A\u0430. \u041C\u043E\u043B\u044F \u043E\u043F\u0438\u0442\u0430\u0439\u0442\u0435 \u043E\u0442\u043D\u043E\u0432\u043E.",
    },
  },
  properties: {
    title: "Търсене на имоти",
    subtitle: "Намерете най-добрите оферти на пазара",
    filtersTitle: "Филтри за търсене",
    searchLabel: "Търсене по ключови думи",
    searchPlaceholder: "Квартал, описание, адрес...",
    cityLabel: "Град",
    cityPlaceholder: "Град",
    allCities: "Всички градове",
    propertyTypeLabel: "Тип имот",
    allTypes: "Всички типове",
    roomsLabel: "Брой стаи",
    allRooms: "Всички",
    roomsSuffix: "стаи",
    areaLabel: "Площ (м²)",
    priceLabel: "Цена (€)",
    pricePerSqmLabel: "Цена за м² (€)",
    from: "От",
    to: "До",
    minPrice: "Мин. цена",
    maxPrice: "Макс. цена",
    minArea: "Мин. м²",
    maxArea: "Макс. м²",
    searchButton: "Търсене",
    reset: "Изчисти",
    noResults: "Няма намерени имоти",
    showing: "Показано",
    of: "от",
    sortLabel: "Сортиране:",
    sortBest: "Най-добрите",
    sortNew: "Нови",
    sortMore: "Сортирай по...",
    sortPriceAsc: "Цена ↑",
    sortPriceDesc: "Цена ↓",
    sortAiValuation: "AI оценка",
    sortArea: "Площад",
    sortPricePerSqm: "Цена/м²",
    sortHot: "Горещи оферти 🔥",
    sortHotTooltip: "Достъпно само за Апартаменти",
    advancedFilters: "Допълнителни филтри",
    advancedActive: "Активни",
    floorLabel: "Етаж",
    constructionYearLabel: "Година на строителство",
    buildingMaterialsLabel: "Вид строителство",
    buildingMaterialsAll: "Всички",
    gasLabel: "Газ",
    gasOnly: "Само с газ",
    centralHeatingLabel: "ТЕЦ",
    centralHeatingOnly: "Само с ТЕЦ",
    selectApartments: "Изберете тип \"Апартаменти\"",
  },
  propertyDetail: {
    backToSearch: "Назад към търсенето",
    notFound: "Имотът не е намерен",
    description: "Описание",
    characteristics: "Характеристики",
    pricePerSqm: "Цена/м²",
    area: "Площ",
    bedrooms: "Стаи",
    propertyType: "Тип имот",
    floor: "Етаж",
    construction: "Строителство",
    buildingMaterials: {
      brick: "Тухла",
      panel: "Панел",
      monolith: "Монолит",
      epk: "ЕПК",
      other: "Друго",
    },
    daysOnMarket: "Публикувана",
    today: "Днес",
    day: "ден",
    days: "дни",
    viewOriginal: "Виж в източника",
    save: "Запази",
    potentialSavings: "Потенциални спестявания",
    listingPrice: "Цена в обявата",
    aiValuation: "AI Оценка",
    priceDeviation: "Ценово отклонение",
    similarProperties: "Подобни имоти",
    utilityCost: "Комунални разходи",
    priceComparison: "спрямо район",
    vsAvg: "спрямо средна",
    priceTrend: "Ценова тенденция в района",
    last6Months: "Последни 6 месеца",
    districtAvg: "Средна за района",
    location: "Местоположение",
    openMaps: "Отвори в Maps",
    noLocation: "Няма местоположение",
    contact: "Контакт",
    ownerContacts: "Контакти на собственика",
    name: "Име",
    phone: "Телефон",
    email: "Имейл",
    district: "Район",
    noContactInfo: "Няма налична информация за контакт",
    moreDetails: "Подробности",
    districtRatings: {
      traffic: "Трафик",
      publicTransport: "Обществен транспорт",
      shops: "Магазини",
      schools: "Училища",
    },
  },
  propertyCard: {
    aiEstimate: "AI оценка",
    goodDeal: "Изгодна цена",
    overpriced: "Завишена цена",
    belowMarket: "под пазарната",
    aboveMarket: "над пазарната",
    fairPrice: "Справедлива цена",
    significantlyBelow: "Значително под пазарната цена",
    significantlyAbove: "Значително над пазарната цена",
    rooms: "стаи",
    types: {
      apartments: "Апартамент",
      house: "Къща",
      studio: "Студио",
      garage: "Гараж",
      land: "Парцел",
      commercial: "Търговски обект",
      other: "Имот",
    },
  },
  servicePage: {
    pageTitle: "Услуги",
    backToServices: "Обратно към услугите",
    details: "Подробности",
    order: "Поръчай",
    consultation: "Консултация",
    addToCart: "Добави в кошницата",
    alreadyInCart: "Вече е в кошницата",
    addedToCart: "Добавено в кошницата",
    mostPopular: "Най-популярна",
    cart: "Кошница",
    cartEmpty: "Кошницата е празна",
    browseServices: "Разгледай услугите",
    name: "Име *",
    email: "Имейл *",
    phone: "Телефон",
    notes: "Бележки",
    paymentNote: "* Заплащането се извършва след обсъждане на детайлите с нашия екип.",
    confirmOrder: "Потвърди поръчката",
    cancel: "Отказ",
    orderAccepted: "Поръчката е приета!",
    orderAcceptedDesc: "Ще се свържем с вас по електронна поща в най-кратък срок за обсъждане на детайлите.",
    ok: "Добре",
    fillNameEmail: "Моля, попълнете име и имейл",
    orderError: "Грешка при поръчката",
    waitSecurity: "Моля, изчакайте проверката за сигурност",
    cartOrderNote: "Поръчка от кошницата",
    consultationBtn: "Консултация по услугите",
    consultationTitle: "Консултация по услугите",
    consultationName: "Име *",
    consultationEmail: "Имейл *",
    consultationPhone: "Телефон",
    consultationMessage: "Вашият въпрос *",
    consultationSubmit: "Изпратете",
    consultationSuccess: "Успешно!",
    consultationSuccessDesc: "Ще се свържем с вас скоро.",
    consultationError: "Грешка",
    consultationErrorDesc: "Моля, опитайте отново.",
  },
};

const ru = {
  nav: {
    home: "Главная",
    deals: "Поиск",
    analytics: "\u0410\u043D\u0430\u043B\u0438\u0442\u0438\u043A\u0430",
    howItWorks: "\u041A\u0430\u043A \u044D\u0442\u043E \u0440\u0430\u0431\u043E\u0442\u0430\u0435\u0442",
    signIn: "\u0412\u0445\u043E\u0434",
    getStarted: "\u041D\u0430\u0447\u0430\u0442\u044C",
    dashboard: "\u041F\u0430\u043D\u0435\u043B\u044C",
    myFilters: "\u041C\u043E\u0438 \u0444\u0438\u043B\u044C\u0442\u0440\u044B",
    myAlerts: "\u041C\u043E\u0438 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
    signOut: "Выход",
    favorites: "Избранное",
  },
  hero: {
    title: "Покупаете недвижимость? Мы на Вашей стороне.",
    subtitle:
      "Единственная услуга в Болгарии, объединяющая AI-оценку с точностью 97% и профессиональных переговорщиков, чтобы сэкономить вам тысячи евро и нервы.",
    ctaPrimary: "Найти и оценить",
    ctaSecondary: "Смотреть услуги",
    stats: {
      properties: "Проанализировано объектов",
      avgSavings: "Средняя экономия на сделке благодаря нашему алгоритму",
      activeAlerts: "Активных уведомлений",
      citiesCovered: "Покрыто городов",
    },
    banners: {
      banner1Title: "Боитесь переплатить за квартиру в Болгарии?",
      banner1Description:
        "Мы бесплатно посчитаем сколько на самом деле стоит квадратный метр в выбранном вами районе или городе",
      banner1Bonus: "Также вы получите скидку -20% на любую нашу услугу",
      banner1Cta: "Рассчитать",
      banner2Title: "Инвестируете в болгарскую недвижимость?",
      banner2Description:
        "Бесплатно получите закрытую аналитику: тепловую карту стоимости м² и реальный годовой прирост цен (ROI) по районам Софии или побережья",
      banner2Bonus: "Также вы получите скидку -20% на любую нашу услугу",
      banner2Cta: "Получить аналитику",
    },
  },
  marketProblem: {
    title: "Рынок работает против покупателя. Мы меняем правила.",
    description:
      "Обычные брокеры защищают продавца или свою комиссию. Dealigator работает только на вас — мы снижаем цену и гарантируем чистоту сделки.",
    standard: {
      title: "Стандарт",
      seller: "Продавец",
      sellerBroker: "Брокер продавца",
      wantHighPrice: "Оба хотят высокую цену",
      vs: "VS",
      lonelyBuyer: "Одинокий покупатель",
    },
    dealigator: {
      title: "С Dealigator",
      buyer: "Покупатель",
      aiLawyerNegotiator: "AI + Юрист + Переговорщик",
      fullProtection: "Полная защита ваших интересов",
      vs: "VS",
      seller: "Продавец",
    },
  },
  secretWeapon: {
    title: "Мы знаем реальную цену до переговоров.",
    description: "Наш алгоритм анализирует 50+ факторов и оценивает объект с точностью 97%.",
    benefit:
      'Как это вам помогает? Вы идёте на просмотр с "козырем в рукаве" — точно знаете, сколько стоит жильё, и имеете аргументы для снижения цены.',
  },
  services: {
    title: "Полная защита от поиска до ключа",
    aiAnalysis: {
      title: "AI Оценка и Анализ",
      description:
        "Находим недооценённые объекты и показываем их реальную стоимость, скрытые риски и потенциал аренды.",
    },
    negotiation: {
      title: "Переговоры и Сделка",
      description:
        "Наш эксперт ведёт переговоры за вас. Цель: снизить цену на 10-15%. Если не снизим — услуга бесплатна.",
    },
    legal: {
      title: "Юридическая безопасность",
      description: "Проверяем объект на обременения, готовим договоры и защищаем ваши деньги на эскроу-счёте.",
    },
  },
  battle: {
    title: "Почему выбирают Dealigator?",
    broker: "Обычный брокер",
    dealigator: "Dealigator",
    interest: { label: "Чьи интересы?", broker: "Продавца / Свои", ai: "Только Ваши (Покупателя)" },
    priceEval: { label: "Оценка цены", broker: '"На глаз" / Со слов продавца', ai: "AI Алгоритм (97% точность)" },
    goalNeg: { label: "Цель переговоров", broker: "Быстрая сделка", ai: "Максимальная скидка" },
    legalCheck: { label: "Юр. проверка", broker: "Поверхностная", ai: "Глубокая (Обременения, история)" },
    pricing: {
      label: "Цена",
      broker: "3-6% комиссия",
      ai: "2.5% или фиксированная комиссия (в зависимости от услуги)",
    },
  },
  featuredDeals: {
    badge: "Новое",
    title: "Последние объявления",
    subtitle: "Самые новые апартаменты на рынке — будьте первыми, кто их увидит.",
    viewAll: "Все объявления",
    belowMarket: "ниже рынка",
    beds: "спален",
    baths: "ванных",
    viewDeal: "Смотреть",
  },
  analyticsPreview: {
    badge: "\u0423\u043C\u043D\u0430\u044F \u0430\u043D\u0430\u043B\u0438\u0442\u0438\u043A\u0430",
    title:
      "\u0412\u0430\u0448\u0430 \u043F\u0435\u0440\u0441\u043E\u043D\u0430\u043B\u044C\u043D\u0430\u044F \u0430\u043D\u0430\u043B\u0438\u0442\u0438\u0447\u0435\u0441\u043A\u0430\u044F \u043F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0430",
    subtitle:
      "\u0413\u043B\u0443\u0431\u043E\u043A\u0438\u0439 \u0430\u043D\u0430\u043B\u0438\u0437 \u0440\u044B\u043D\u043A\u0430, \u043F\u0440\u043E\u0433\u043D\u043E\u0437\u044B \u0446\u0435\u043D \u0438 \u0430\u043D\u0430\u043B\u0438\u0437 \u0442\u0440\u0435\u043D\u0434\u043E\u0432 \u0434\u043B\u044F \u043F\u0440\u0438\u043D\u044F\u0442\u0438\u044F \u043E\u0431\u043E\u0441\u043D\u043E\u0432\u0430\u043D\u043D\u044B\u0445 \u0438\u043D\u0432\u0435\u0441\u0442\u0438\u0446\u0438\u043E\u043D\u043D\u044B\u0445 \u0440\u0435\u0448\u0435\u043D\u0438\u0439.",
    features: {
      priceAnalytics: {
        title: "\u0426\u0435\u043D\u043E\u0432\u0430\u044F \u0430\u043D\u0430\u043B\u0438\u0442\u0438\u043A\u0430",
        description:
          "\u0418\u0441\u0442\u043E\u0440\u0438\u0447\u0435\u0441\u043A\u0438\u0435 \u0442\u0440\u0435\u043D\u0434\u044B \u0438 \u043F\u0440\u043E\u0433\u043D\u043E\u0437\u044B",
      },
      heatMaps: {
        title: "\u0422\u0435\u043F\u043B\u043E\u0432\u044B\u0435 \u043A\u0430\u0440\u0442\u044B",
        description:
          "\u0412\u0438\u0437\u0443\u0430\u043B\u0438\u0437\u0438\u0440\u0443\u0439\u0442\u0435 \u0433\u043E\u0440\u044F\u0447\u0438\u0435 \u0437\u043E\u043D\u044B",
      },
      roiCalculator: {
        title: "\u041A\u0430\u043B\u044C\u043A\u0443\u043B\u044F\u0442\u043E\u0440 ROI",
        description:
          "\u041E\u0446\u0435\u043D\u0438\u0442\u0435 \u0434\u043E\u0445\u043E\u0434\u043D\u043E\u0441\u0442\u044C \u0438\u043D\u0432\u0435\u0441\u0442\u0438\u0446\u0438\u0439",
      },
      dealAlerts: {
        title:
          "\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u043E \u0441\u0434\u0435\u043B\u043A\u0430\u0445",
        description:
          "\u041C\u0433\u043D\u043E\u0432\u0435\u043D\u043D\u044B\u0435 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u043E \u043F\u043E\u0434\u0445\u043E\u0434\u044F\u0449\u0438\u0445 \u0441\u0434\u0435\u043B\u043A\u0430\u0445",
      },
    },
    exploreButton: "\u0418\u0437\u0443\u0447\u0438\u0442\u044C \u0430\u043D\u0430\u043B\u0438\u0442\u0438\u043A\u0443",
    marketOverview: "\u041E\u0431\u0437\u043E\u0440 \u0440\u044B\u043D\u043A\u0430 \u0421\u043E\u0444\u0438\u0438",
    pricePerSqm: "\u0426\u0435\u043D\u0430/\u043C\u00B2",
    listings: "\u041E\u0431\u044A\u044F\u0432\u043B\u0435\u043D\u0438\u0439",
    avgDays: "\u0421\u0440\u0435\u0434\u043D. \u0434\u043D\u0435\u0439",
    newDealFound: "\u041D\u043E\u0432\u0430\u044F \u0441\u0434\u0435\u043B\u043A\u0430!",
    matchesFilters:
      "\u0421\u043E\u043E\u0442\u0432\u0435\u0442\u0441\u0442\u0432\u0443\u0435\u0442 \u0432\u0430\u0448\u0438\u043C \u0444\u0438\u043B\u044C\u0442\u0440\u0430\u043C",
  },
  howItWorks: {
    badge: "\u041F\u0440\u043E\u0441\u0442\u043E\u0439 \u043F\u0440\u043E\u0446\u0435\u0441\u0441",
    title: "\u041A\u0430\u043A \u0440\u0430\u0431\u043E\u0442\u0430\u0435\u0442 Dealigator",
    subtitle:
      "\u0422\u0440\u0438 \u043F\u0440\u043E\u0441\u0442\u044B\u0445 \u0448\u0430\u0433\u0430 \u043A \u0438\u0434\u0435\u0430\u043B\u044C\u043D\u043E\u0439 \u0441\u0434\u0435\u043B\u043A\u0435 \u0441 \u043D\u0435\u0434\u0432\u0438\u0436\u0438\u043C\u043E\u0441\u0442\u044C\u044E.",
    steps: {
      collect: {
        step: "01",
        title: "\u041C\u044B \u0441\u043E\u0431\u0438\u0440\u0430\u0435\u043C \u0434\u0430\u043D\u043D\u044B\u0435",
        description:
          "\u041D\u0430\u0448\u0430 \u0441\u0438\u0441\u0442\u0435\u043C\u0430 \u0430\u0433\u0440\u0435\u0433\u0438\u0440\u0443\u0435\u0442 \u043E\u0431\u044A\u044F\u0432\u043B\u0435\u043D\u0438\u044F \u0438\u0437 20+ \u0438\u0441\u0442\u043E\u0447\u043D\u0438\u043A\u043E\u0432 \u043F\u043E \u0411\u043E\u043B\u0433\u0430\u0440\u0438\u0438, \u043E\u0431\u043D\u043E\u0432\u043B\u044F\u044F \u043A\u0430\u0436\u0434\u044B\u0439 \u0447\u0430\u0441.",
      },
      analyze: {
        step: "02",
        title: "AI \u0430\u043D\u0430\u043B\u0438\u0437\u0438\u0440\u0443\u0435\u0442",
        description:
          "\u041C\u043E\u0434\u0435\u043B\u0438 \u043C\u0430\u0448\u0438\u043D\u043D\u043E\u0433\u043E \u043E\u0431\u0443\u0447\u0435\u043D\u0438\u044F \u043E\u0446\u0435\u043D\u0438\u0432\u0430\u044E\u0442 \u043A\u0430\u0436\u0434\u044B\u0439 \u043E\u0431\u044A\u0435\u043A\u0442 \u043F\u043E \u0440\u044B\u043D\u043E\u0447\u043D\u044B\u043C \u0434\u0430\u043D\u043D\u044B\u043C \u0434\u043B\u044F \u043F\u043E\u0438\u0441\u043A\u0430 \u043D\u0435\u0434\u043E\u043E\u0446\u0435\u043D\u0451\u043D\u043D\u044B\u0445 \u043D\u0430\u0445\u043E\u0434\u043E\u043A.",
      },
      alert: {
        step: "03",
        title:
          "\u0412\u044B \u043F\u043E\u043B\u0443\u0447\u0430\u0435\u0442\u0435 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435",
        description:
          "\u041F\u043E\u043B\u0443\u0447\u0430\u0439\u0442\u0435 \u043C\u0433\u043D\u043E\u0432\u0435\u043D\u043D\u044B\u0435 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F, \u043A\u043E\u0433\u0434\u0430 \u043E\u0431\u044A\u0435\u043A\u0442\u044B \u043F\u043E \u0432\u0430\u0448\u0438\u043C \u043A\u0440\u0438\u0442\u0435\u0440\u0438\u044F\u043C \u043F\u043E\u044F\u0432\u043B\u044F\u044E\u0442\u0441\u044F \u043D\u0438\u0436\u0435 \u0440\u044B\u043D\u043E\u0447\u043D\u043E\u0439 \u0441\u0442\u043E\u0438\u043C\u043E\u0441\u0442\u0438.",
      },
    },
  },
  footer: {
    cta: {
      title:
        "\u0413\u043E\u0442\u043E\u0432\u044B \u043A \u0441\u043B\u0435\u0434\u0443\u044E\u0449\u0435\u0439 \u0441\u0434\u0435\u043B\u043A\u0435?",
      subtitle:
        "\u041F\u0440\u0438\u0441\u043E\u0435\u0434\u0438\u043D\u044F\u0439\u0442\u0435\u0441\u044C \u043A \u0442\u044B\u0441\u044F\u0447\u0430\u043C \u0438\u043D\u0432\u0435\u0441\u0442\u043E\u0440\u043E\u0432, \u043A\u043E\u0442\u043E\u0440\u044B\u0435 \u0443\u0436\u0435 \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u0443\u044E\u0442 Dealigator \u0434\u043B\u044F \u043F\u043E\u0438\u0441\u043A\u0430 \u043D\u0435\u0434\u043E\u043E\u0446\u0435\u043D\u0451\u043D\u043D\u043E\u0439 \u043D\u0435\u0434\u0432\u0438\u0436\u0438\u043C\u043E\u0441\u0442\u0438.",
      emailPlaceholder: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 email",
      button: "\u041D\u0430\u0447\u0430\u0442\u044C \u0431\u0435\u0441\u043F\u043B\u0430\u0442\u043D\u043E",
    },
    brand: {
      description:
        "Умный способ покупки недвижимости. AI-оценка, юридическая защита и профессиональные переговоры по цене. Мы работаем только на вас, а не на продавца.",
    },
    platform: {
      title: "\u041F\u043B\u0430\u0442\u0444\u043E\u0440\u043C\u0430",
      search: "\u041F\u043E\u0438\u0441\u043A \u0441\u0434\u0435\u043B\u043E\u043A",
      analytics: "\u0410\u043D\u0430\u043B\u0438\u0442\u0438\u043A\u0430",
      alerts:
        "\u0426\u0435\u043D\u043E\u0432\u044B\u0435 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
      api: "API \u0434\u043E\u0441\u0442\u0443\u043F",
    },
    resources: {
      title: "\u0420\u0435\u0441\u0443\u0440\u0441\u044B",
      blog: "\u0411\u043B\u043E\u0433",
      guides: "\u0420\u044B\u043D\u043E\u0447\u043D\u044B\u0435 \u0433\u0438\u0434\u044B",
      support: "\u041F\u043E\u0434\u0434\u0435\u0440\u0436\u043A\u0430",
      pricing: "\u0426\u0435\u043D\u044B",
    },
    contact: {
      title: "\u041A\u043E\u043D\u0442\u0430\u043A\u0442\u044B",
      location: "\u0421\u043E\u0444\u0438\u044F, \u0411\u043E\u043B\u0433\u0430\u0440\u0438\u044F",
      feedback: "Форма обратной связи",
    },
    legal: {
      rights: "\u0412\u0441\u0435 \u043F\u0440\u0430\u0432\u0430 \u0437\u0430\u0449\u0438\u0449\u0435\u043D\u044B.",
      privacy:
        "\u041F\u043E\u043B\u0438\u0442\u0438\u043A\u0430 \u043A\u043E\u043D\u0444\u0438\u0434\u0435\u043D\u0446\u0438\u0430\u043B\u044C\u043D\u043E\u0441\u0442\u0438",
      terms:
        "\u0423\u0441\u043B\u043E\u0432\u0438\u044F \u0438\u0441\u043F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u043D\u0438\u044F",
    },
  },
  contact: {
    back: "Назад",
    title: "Свяжитесь с нами",
    subtitle: "Мы будем рады услышать вас. Отправьте нам сообщение, и мы ответим как можно скорее.",
    formTitle: "Отправить сообщение",
    formDescription: "Заполните форму ниже, и мы свяжемся с вами в ближайшее время.",
    name: "Имя",
    namePlaceholder: "Ваше имя",
    email: "Email",
    emailPlaceholder: "your@email.com",
    subject: "Тема",
    subjectPlaceholder: "Чем мы можем помочь?",
    message: "Сообщение",
    messagePlaceholder: "Расскажите подробнее о вашем запросе...",
    submit: "Отправить сообщение",
    sending: "Отправка...",
    successTitle: "Сообщение отправлено",
    successDescription: "Спасибо за ваше сообщение. Мы свяжемся с вами в ближайшее время!",
    errors: {
      nameMin: "Имя должно быть не менее 2 символов",
      nameMax: "Имя должно быть менее 100 символов",
      invalidEmail: "Пожалуйста, введите корректный email",
      emailMax: "Email должен быть менее 255 символов",
      subjectMin: "Тема должна быть не менее 3 символов",
      subjectMax: "Тема должна быть менее 200 символов",
      messageMin: "Сообщение должно быть не менее 10 символов",
      messageMax: "Сообщение должно быть менее 2000 символов",
      captchaRequired: "Требуется captcha",
      captchaDescription: "Пожалуйста, пройдите проверку captcha",
      submitFailed: "Ошибка отправки",
      tryAgain: "Пожалуйста, попробуйте позже",
    },
  },
  auth: {
    backToHome: "\u041D\u0430 \u0433\u043B\u0430\u0432\u043D\u0443\u044E",
    signIn: "\u0412\u0445\u043E\u0434",
    signUp: "\u0420\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F",
    signInDesc:
      "\u0412\u043E\u0439\u0434\u0438\u0442\u0435 \u0434\u043B\u044F \u0434\u043E\u0441\u0442\u0443\u043F\u0430 \u043A \u0441\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u043C \u0444\u0438\u043B\u044C\u0442\u0440\u0430\u043C \u0438 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F\u043C",
    signUpDesc:
      "\u0417\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u0443\u0439\u0442\u0435\u0441\u044C \u0434\u043B\u044F \u043E\u0442\u0441\u043B\u0435\u0436\u0438\u0432\u0430\u043D\u0438\u044F \u043B\u0443\u0447\u0448\u0438\u0445 \u0441\u0434\u0435\u043B\u043E\u043A",
    name: "\u0418\u043C\u044F",
    namePlaceholder: "\u0412\u0430\u0448\u0435 \u0438\u043C\u044F",
    email: "Email",
    emailPlaceholder: "email@example.com",
    password: "\u041F\u0430\u0440\u043E\u043B\u044C",
    loading: "\u0417\u0430\u0433\u0440\u0443\u0437\u043A\u0430...",
    or: "\u0438\u043B\u0438",
    continueWithGoogle: "\u041F\u0440\u043E\u0434\u043E\u043B\u0436\u0438\u0442\u044C \u0441 Google",
    noAccount: "\u041D\u0435\u0442 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430?",
    haveAccount: "\u0423\u0436\u0435 \u0435\u0441\u0442\u044C \u0430\u043A\u043A\u0430\u0443\u043D\u0442?",
    invalidEmail:
      "\u041F\u043E\u0436\u0430\u043B\u0443\u0439\u0441\u0442\u0430, \u0432\u0432\u0435\u0434\u0438\u0442\u0435 \u043A\u043E\u0440\u0440\u0435\u043A\u0442\u043D\u044B\u0439 email",
    passwordMin:
      "\u041F\u0430\u0440\u043E\u043B\u044C \u0434\u043E\u043B\u0436\u0435\u043D \u0431\u044B\u0442\u044C \u043D\u0435 \u043C\u0435\u043D\u0435\u0435 6 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432",
    nameMin:
      "\u0418\u043C\u044F \u0434\u043E\u043B\u0436\u043D\u043E \u0431\u044B\u0442\u044C \u043D\u0435 \u043C\u0435\u043D\u0435\u0435 2 \u0441\u0438\u043C\u0432\u043E\u043B\u043E\u0432",
    loginError: "\u041E\u0448\u0438\u0431\u043A\u0430 \u0432\u0445\u043E\u0434\u0430",
    invalidCredentials:
      "\u041D\u0435\u0432\u0435\u0440\u043D\u044B\u0439 email \u0438\u043B\u0438 \u043F\u0430\u0440\u043E\u043B\u044C",
    error: "\u041E\u0448\u0438\u0431\u043A\u0430",
    welcomeBack: "\u0421 \u0432\u043E\u0437\u0432\u0440\u0430\u0449\u0435\u043D\u0438\u0435\u043C!",
    signedIn: "\u0423\u0441\u043F\u0435\u0448\u043D\u044B\u0439 \u0432\u0445\u043E\u0434",
    userExists:
      "\u041F\u043E\u043B\u044C\u0437\u043E\u0432\u0430\u0442\u0435\u043B\u044C \u0441\u0443\u0449\u0435\u0441\u0442\u0432\u0443\u0435\u0442",
    userExistsDesc:
      "\u042D\u0442\u043E\u0442 email \u0443\u0436\u0435 \u0437\u0430\u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0438\u0440\u043E\u0432\u0430\u043D. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u0432\u043E\u0439\u0442\u0438.",
    registrationError:
      "\u041E\u0448\u0438\u0431\u043A\u0430 \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u0438",
    registrationSuccess:
      "\u0423\u0441\u043F\u0435\u0448\u043D\u0430\u044F \u0440\u0435\u0433\u0438\u0441\u0442\u0440\u0430\u0446\u0438\u044F",
    accountCreated:
      "\u0410\u043A\u043A\u0430\u0443\u043D\u0442 \u0441\u043E\u0437\u0434\u0430\u043D. \u0414\u043E\u0431\u0440\u043E \u043F\u043E\u0436\u0430\u043B\u043E\u0432\u0430\u0442\u044C!",
    somethingWrong:
      "\u0427\u0442\u043E-\u0442\u043E \u043F\u043E\u0448\u043B\u043E \u043D\u0435 \u0442\u0430\u043A. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u0441\u043D\u043E\u0432\u0430.",
    googleError:
      "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u0432\u043E\u0439\u0442\u0438 \u0447\u0435\u0440\u0435\u0437 Google",
    confirmEmail: "Подтвердите вашу почту",
    confirmEmailSent: "Мы отправили ссылку для подтверждения на",
    confirmEmailDesc: "Пожалуйста, проверьте входящие письма и нажмите на ссылку, чтобы активировать аккаунт.",
    checkSpam: 'Проверьте папку "Спам", если не видите письмо.',
    backToLogin: "Вернуться ко входу",
  },
  dashboard: {
    back: "\u041D\u0430\u0437\u0430\u0434",
    signOut: "\u0412\u044B\u0445\u043E\u0434",
    title: "\u041F\u0430\u043D\u0435\u043B\u044C \u0443\u043F\u0440\u0430\u0432\u043B\u0435\u043D\u0438\u044F",
    tabs: {
      filters:
        "\u0421\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u0435 \u0444\u0438\u043B\u044C\u0442\u0440\u044B",
      alerts: "\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
      profile: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C",
    },
    filters: {
      title:
        "\u0421\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u0435 \u0444\u0438\u043B\u044C\u0442\u0440\u044B",
      description:
        "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0439\u0442\u0435 \u0441\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u043C\u0438 \u0444\u0438\u043B\u044C\u0442\u0440\u0430\u043C\u0438 \u043F\u043E\u0438\u0441\u043A\u0430",
      create: "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440",
      noFilters:
        "\u041D\u0435\u0442 \u0441\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u0445 \u0444\u0438\u043B\u044C\u0442\u0440\u043E\u0432",
      noFiltersDesc:
        "\u0421\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u043F\u0435\u0440\u0432\u044B\u0439 \u0444\u0438\u043B\u044C\u0442\u0440 \u0434\u043B\u044F \u043E\u0442\u0441\u043B\u0435\u0436\u0438\u0432\u0430\u043D\u0438\u044F \u043D\u0435\u0434\u0432\u0438\u0436\u0438\u043C\u043E\u0441\u0442\u0438.",
      location: "\u041C\u0435\u0441\u0442\u043E\u043F\u043E\u043B\u043E\u0436\u0435\u043D\u0438\u0435",
      priceRange: "\u0426\u0435\u043D\u043E\u0432\u043E\u0439 \u0434\u0438\u0430\u043F\u0430\u0437\u043E\u043D",
      area: "\u041F\u043B\u043E\u0449\u0430\u0434\u044C",
      type: "\u0422\u0438\u043F",
      rooms: "\u043A\u043E\u043C\u043D\u0430\u0442",
      edit: "\u0418\u0437\u043C\u0435\u043D\u0438\u0442\u044C",
      delete: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C",
      filterName: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0444\u0438\u043B\u044C\u0442\u0440\u0430",
      filterNamePlaceholder:
        "\u043D\u0430\u043F\u0440. \u041A\u0432\u0430\u0440\u0442\u0438\u0440\u044B \u0432 \u0446\u0435\u043D\u0442\u0440\u0435 \u0421\u043E\u0444\u0438\u0438",
      locationLabel: "\u041C\u0435\u0441\u0442\u043E\u043F\u043E\u043B\u043E\u0436\u0435\u043D\u0438\u0435",
      locationPlaceholder: "\u0413\u043E\u0440\u043E\u0434 \u0438\u043B\u0438 \u0440\u0430\u0439\u043E\u043D",
      minPrice: "\u041C\u0438\u043D \u0446\u0435\u043D\u0430 (\u20AC)",
      maxPrice: "\u041C\u0430\u043A\u0441 \u0446\u0435\u043D\u0430 (\u20AC)",
      minArea: "\u041C\u0438\u043D \u043F\u043B\u043E\u0449\u0430\u0434\u044C (\u043C\u00B2)",
      maxArea: "\u041C\u0430\u043A\u0441 \u043F\u043B\u043E\u0449\u0430\u0434\u044C (\u043C\u00B2)",
      propertyType: "\u0422\u0438\u043F \u043D\u0435\u0434\u0432\u0438\u0436\u0438\u043C\u043E\u0441\u0442\u0438",
      selectType: "\u0412\u044B\u0431\u0435\u0440\u0438\u0442\u0435 \u0442\u0438\u043F",
      apartment: "\u041A\u0432\u0430\u0440\u0442\u0438\u0440\u0430",
      house: "\u0414\u043E\u043C",
      studio: "\u0421\u0442\u0443\u0434\u0438\u044F",
      commercial: "\u041A\u043E\u043C\u043C\u0435\u0440\u0447\u0435\u0441\u043A\u0430\u044F",
      land: "\u0417\u0435\u043C\u043B\u044F",
      roomsLabel: "\u041A\u043E\u043C\u043D\u0430\u0442\u044B",
      saveFilter: "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0444\u0438\u043B\u044C\u0442\u0440",
      cancel: "\u041E\u0442\u043C\u0435\u043D\u0430",
    },
    alerts: {
      title: "\u041C\u043E\u0438 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
      description:
        "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u0442\u0435 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u043E \u043D\u043E\u0432\u044B\u0445 \u0441\u0434\u0435\u043B\u043A\u0430\u0445",
      create:
        "\u0421\u043E\u0437\u0434\u0430\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435",
      noAlerts:
        "\u041D\u0435\u0442 \u043D\u0430\u0441\u0442\u0440\u043E\u0435\u043D\u043D\u044B\u0445 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0439",
      noAlertsDesc:
        "\u0421\u043E\u0437\u0434\u0430\u0439\u0442\u0435 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435 \u043E \u043D\u043E\u0432\u044B\u0445 \u0441\u0434\u0435\u043B\u043A\u0430\u0445 \u043F\u043E \u0432\u0430\u0448\u0438\u043C \u043A\u0440\u0438\u0442\u0435\u0440\u0438\u044F\u043C.",
      active: "\u0410\u043A\u0442\u0438\u0432\u043D\u043E",
      linkedFilter: "\u0421\u0432\u044F\u0437\u0430\u043D\u043D\u044B\u0439 \u0444\u0438\u043B\u044C\u0442\u0440",
      minScore: "\u041C\u0438\u043D \u0441\u043A\u043E\u0440 \u0441\u0434\u0435\u043B\u043A\u0438",
      maxDeviation:
        "\u041C\u0430\u043A\u0441 \u0440\u044B\u043D\u043E\u0447\u043D\u043E\u0435 \u043E\u0442\u043A\u043B\u043E\u043D\u0435\u043D\u0438\u0435",
      notifications: "\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
      email: "Email",
      push: "Push",
      delete: "\u0423\u0434\u0430\u043B\u0438\u0442\u044C",
      alertName:
        "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
      alertNamePlaceholder:
        "\u043D\u0430\u043F\u0440. \u0413\u043E\u0440\u044F\u0447\u0438\u0435 \u0441\u0434\u0435\u043B\u043A\u0438 \u0432 \u0421\u043E\u0444\u0438\u0438",
      selectFilter:
        "\u0421\u0432\u044F\u0437\u0430\u043D\u043D\u044B\u0439 \u0444\u0438\u043B\u044C\u0442\u0440 (\u043E\u043F\u0446\u0438\u043E\u043D\u0430\u043B\u044C\u043D\u043E)",
      noFilter: "\u0411\u0435\u0437 \u0444\u0438\u043B\u044C\u0442\u0440\u0430",
      minDealScore:
        "\u041C\u0438\u043D\u0438\u043C\u0430\u043B\u044C\u043D\u044B\u0439 \u0441\u043A\u043E\u0440 \u0441\u0434\u0435\u043B\u043A\u0438",
      maxPriceDeviation:
        "\u041C\u0430\u043A\u0441 \u0446\u0435\u043D\u0430 \u043E\u0442\u043D\u043E\u0441\u0438\u0442\u0435\u043B\u044C\u043D\u043E \u0440\u044B\u043D\u043A\u0430 (%)",
      notifyEmail: "Email \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
      notifyPush: "Push \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F",
      saveAlert:
        "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435",
      cancel: "\u041E\u0442\u043C\u0435\u043D\u0430",
    },
    profile: {
      title: "\u041D\u0430\u0441\u0442\u0440\u043E\u0439\u043A\u0438 \u043F\u0440\u043E\u0444\u0438\u043B\u044F",
      description:
        "\u0423\u043F\u0440\u0430\u0432\u043B\u044F\u0439\u0442\u0435 \u0438\u043D\u0444\u043E\u0440\u043C\u0430\u0446\u0438\u0435\u0439 \u0430\u043A\u043A\u0430\u0443\u043D\u0442\u0430",
      fullName: "\u041F\u043E\u043B\u043D\u043E\u0435 \u0438\u043C\u044F",
      fullNamePlaceholder: "\u0412\u0430\u0448\u0435 \u0438\u043C\u044F",
      email: "Email",
      emailPlaceholder: "\u0412\u0430\u0448 email",
      saveChanges:
        "\u0421\u043E\u0445\u0440\u0430\u043D\u0438\u0442\u044C \u0438\u0437\u043C\u0435\u043D\u0435\u043D\u0438\u044F",
      statistics: "\u0421\u0442\u0430\u0442\u0438\u0441\u0442\u0438\u043A\u0430",
      savedFilters:
        "\u0421\u043E\u0445\u0440\u0430\u043D\u0451\u043D\u043D\u044B\u0445 \u0444\u0438\u043B\u044C\u0442\u0440\u043E\u0432",
      activeAlerts:
        "\u0410\u043A\u0442\u0438\u0432\u043D\u044B\u0445 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0439",
      profileUpdated: "\u041F\u0440\u043E\u0444\u0438\u043B\u044C \u043E\u0431\u043D\u043E\u0432\u043B\u0451\u043D",
      profileUpdatedDesc:
        "\u0412\u0430\u0448 \u043F\u0440\u043E\u0444\u0438\u043B\u044C \u0443\u0441\u043F\u0435\u0448\u043D\u043E \u043E\u0431\u043D\u043E\u0432\u043B\u0451\u043D.",
      updateError: "\u041E\u0448\u0438\u0431\u043A\u0430",
      updateErrorDesc:
        "\u041D\u0435 \u0443\u0434\u0430\u043B\u043E\u0441\u044C \u043E\u0431\u043D\u043E\u0432\u0438\u0442\u044C \u043F\u0440\u043E\u0444\u0438\u043B\u044C.",
    },
    toast: {
      filterCreated: "\u0424\u0438\u043B\u044C\u0442\u0440 \u0441\u043E\u0437\u0434\u0430\u043D",
      filterCreatedDesc:
        "\u0412\u0430\u0448 \u0444\u0438\u043B\u044C\u0442\u0440 \u0441\u043E\u0445\u0440\u0430\u043D\u0451\u043D.",
      filterDeleted: "\u0424\u0438\u043B\u044C\u0442\u0440 \u0443\u0434\u0430\u043B\u0451\u043D",
      filterDeletedDesc:
        "\u0424\u0438\u043B\u044C\u0442\u0440 \u0431\u044B\u043B \u0443\u0434\u0430\u043B\u0451\u043D.",
      alertCreated:
        "\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435 \u0441\u043E\u0437\u0434\u0430\u043D\u043E",
      alertCreatedDesc:
        "\u0412\u044B \u0431\u0443\u0434\u0435\u0442\u0435 \u043F\u043E\u043B\u0443\u0447\u0430\u0442\u044C \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u043E \u043F\u043E\u0434\u0445\u043E\u0434\u044F\u0449\u0438\u0445 \u0441\u0434\u0435\u043B\u043A\u0430\u0445.",
      alertDeleted:
        "\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435 \u0443\u0434\u0430\u043B\u0435\u043D\u043E",
      alertDeletedDesc:
        "\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435 \u0431\u044B\u043B\u043E \u0443\u0434\u0430\u043B\u0435\u043D\u043E.",
      alertUpdated:
        "\u0423\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u0435 \u043E\u0431\u043D\u043E\u0432\u043B\u0435\u043D\u043E",
      alertUpdatedDesc:
        "\u0421\u0442\u0430\u0442\u0443\u0441 \u0443\u0432\u0435\u0434\u043E\u043C\u043B\u0435\u043D\u0438\u044F \u0438\u0437\u043C\u0435\u043D\u0451\u043D.",
      error: "\u041E\u0448\u0438\u0431\u043A\u0430",
      errorDesc:
        "\u041F\u0440\u043E\u0438\u0437\u043E\u0448\u043B\u0430 \u043E\u0448\u0438\u0431\u043A\u0430. \u041F\u043E\u043F\u0440\u043E\u0431\u0443\u0439\u0442\u0435 \u0441\u043D\u043E\u0432\u0430.",
    },
  },
  properties: {
    title: "Поиск недвижимости",
    subtitle: "Найдите лучшие предложения на рынке",
    filtersTitle: "Фильтры поиска",
    searchLabel: "Поиск по ключевым словам",
    searchPlaceholder: "Район, описание, адрес...",
    cityLabel: "Город",
    cityPlaceholder: "Город",
    allCities: "Все города",
    propertyTypeLabel: "Тип жилья",
    allTypes: "Все типы",
    roomsLabel: "Количество комнат",
    allRooms: "Любое",
    roomsSuffix: "комн.",
    areaLabel: "Площадь (м²)",
    priceLabel: "Цена (€)",
    pricePerSqmLabel: "Цена за м² (€)",
    from: "От",
    to: "До",
    minPrice: "Мин. цена",
    maxPrice: "Макс. цена",
    minArea: "Мин. м²",
    maxArea: "Макс. м²",
    searchButton: "Поиск",
    reset: "Сбросить",
    noResults: "Ничего не найдено",
    showing: "Показано",
    of: "из",
    sortLabel: "Сортировка:",
    sortBest: "Лучшие",
    sortNew: "Новые",
    sortMore: "Сортировка",
    sortPriceAsc: "Цена ↑",
    sortPriceDesc: "Цена ↓",
    sortAiValuation: "AI оценка",
    sortArea: "Площадь",
    sortPricePerSqm: "Цена/м²",
    sortHot: "Горячие предложения 🔥",
    sortHotTooltip: "Доступно только для Апартаментов",
    advancedFilters: "Дополнительные фильтры",
    advancedActive: "Активны",
    floorLabel: "Этаж",
    constructionYearLabel: "Год постройки",
    buildingMaterialsLabel: "Тип строительства",
    buildingMaterialsAll: "Все",
    gasLabel: "Газ",
    gasOnly: "Только с газом",
    centralHeatingLabel: "Центр. отопление",
    centralHeatingOnly: "Только с центр. отоплением",
    selectApartments: "Выберите тип \"Апартаменти\"",
  },
  propertyDetail: {
    backToSearch: "Назад к поиску",
    notFound: "Объект не найден",
    description: "Описание",
    characteristics: "Характеристики",
    pricePerSqm: "Цена/м²",
    area: "Площадь",
    bedrooms: "Комнаты",
    propertyType: "Тип недвижимости",
    floor: "Этаж",
    construction: "Строительство",
    buildingMaterials: {
      brick: "Кирпич",
      panel: "Панель",
      monolith: "Монолит",
      epk: "ЕПК",
      other: "Другое",
    },
    daysOnMarket: "Дней на рынке",
    today: "Сегодня",
    day: "день",
    days: "дней",
    viewOriginal: "Смотреть на источнике",
    save: "Сохранить",
    potentialSavings: "Потенциальная экономия",
    listingPrice: "Цена объявления",
    aiValuation: "AI Оценка",
    priceDeviation: "Отклонение цены",
    similarProperties: "Похожие объекты",
    utilityCost: "Коммунальные расходы",
    priceComparison: "vs средняя района",
    vsAvg: "vs средней",
    priceTrend: "Динамика цен в районе",
    last6Months: "Последние 6 месяцев",
    districtAvg: "Средняя по району",
    location: "Расположение",
    openMaps: "Открыть в Maps",
    noLocation: "Местоположение недоступно",
    contact: "Контакт",
    ownerContacts: "Контакты владельца",
    name: "Имя",
    phone: "Телефон",
    email: "Email",
    district: "Район",
    noContactInfo: "Контактная информация недоступна",
    moreDetails: "Подробнее",
    districtRatings: {
      traffic: "Трафик",
      publicTransport: "Общественный транспорт",
      shops: "Магазины",
      schools: "Школы",
    },
  },
  propertyCard: {
    aiEstimate: "AI оценка",
    goodDeal: "Выгодная цена",
    overpriced: "Завышенная цена",
    belowMarket: "ниже рынка",
    aboveMarket: "выше рынка",
    fairPrice: "рыночная цена",
    significantlyBelow: "Значительно ниже рыночной цены",
    significantlyAbove: "Значительно выше рыночной цены",
    rooms: "комнат",
    types: {
      apartments: "Квартира",
      house: "Дом",
      studio: "Студия",
      garage: "Гараж",
      land: "Участок",
      commercial: "Коммерческая",
      other: "Недвижимость",
    },
  },
  servicePage: {
    pageTitle: "Услуги",
    backToServices: "Назад к услугам",
    details: "Подробнее",
    order: "Заказать",
    consultation: "Консультация",
    addToCart: "В корзину",
    alreadyInCart: "Уже в корзине",
    addedToCart: "Добавлено в корзину",
    mostPopular: "Самая популярная",
    cart: "Корзина",
    cartEmpty: "Корзина пуста",
    browseServices: "Посмотреть услуги",
    name: "Имя *",
    email: "Эл. почта *",
    phone: "Телефон",
    notes: "Примечания",
    paymentNote: "* Оплата производится после обсуждения деталей с нашей командой.",
    confirmOrder: "Подтвердить заказ",
    cancel: "Отмена",
    orderAccepted: "Заказ принят!",
    orderAcceptedDesc: "Мы свяжемся с вами по электронной почте в ближайшее время для обсуждения деталей.",
    ok: "Хорошо",
    fillNameEmail: "Пожалуйста, заполните имя и email",
    orderError: "Ошибка при оформлении заказа",
    waitSecurity: "Пожалуйста, дождитесь проверки безопасности",
    cartOrderNote: "Заказ из корзины",
    consultationBtn: "Консультация по услугам",
    consultationTitle: "Консультация по услугам",
    consultationName: "Имя *",
    consultationEmail: "Эл. почта *",
    consultationPhone: "Телефон",
    consultationMessage: "Ваш вопрос *",
    consultationSubmit: "Отправить",
    consultationSuccess: "Успешно!",
    consultationSuccessDesc: "Мы свяжемся с вами в ближайшее время.",
    consultationError: "Ошибка",
    consultationErrorDesc: "Пожалуйста, попробуйте снова.",
  },
};

export const translations = {
  en,
  bg,
  ru,
} as const;

export type TranslationKeys = typeof en;
